import { getFileUrl } from "@/lib/api";
import {
  Building2,
  Calendar,
  Camera,
  Eye,
  FileText,
  Filter,
  MapPin,
  MoreHorizontal,
  ToggleLeft,
  ToggleRight,
  User
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import ActionMenu from "../../../components/shared/ActionMenu";
import DataTable from "../../../components/shared/DataTable";
import LazySearchSelect from "../../../components/shared/LazySearchSelect";
import SecureImage from "../../../components/shared/SecureImage";
import { Button } from "../../../components/ui/button";
import { DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Switch } from "../../../components/ui/switch";
import { formatDate } from "../../../utils/dateUtils";

const getMimeType = (photo) => {
  if (photo.fileType) return photo.fileType;
  const data = photo.photoData;
  if (!data) return "";

  if (data.startsWith("data:")) {
    const match = data.match(/^data:([\w\-\.\/]+);base64,/);
    return match ? match[1] : "";
  }

  const ext = data.split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return `image/${ext === "jpg" ? "jpeg" : ext}`;
  if (ext === "pdf") return "application/pdf";
  return "";
};

const DocumentTable = ({
  documents,
  permissions,
  onDelete,
  onView,
  dateRange,
  onDateRangeChange,
  selectedUser,
  onUserChange,
  selectedType,
  onTypeChange,
  sortBy,
  direction,
  onSort,
  showDeleted,
  setShowDeleted,
  setCurrentPage
}) => {
  const navigate = useNavigate();

  const documentColumns = [
    {
      header: "Type",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${selectedType !== "ALL" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Type</p>
              <select
                value={selectedType}
                onChange={(e) => onTypeChange(e.target.value)}
                className="w-full text-xs border border-slate-200 dark:border-yale-blue-800 rounded p-1 bg-transparent text-foreground"
              >
                <option value="ALL">All Types</option>
                <option value="image">Images</option>
                <option value="application/pdf">PDFs</option>
                <option value="sheet">Excel / Spreadsheets</option>
                <option value="word">Word Documents</option>
              </select>
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "fileType",
      render: (val, doc) => {
        const mimeType = val || getMimeType(doc) || "";
        if (mimeType.startsWith("image/")) {
          return (
            <span className="text-xs font-medium bg-green-50 dark:bg-green-950/30 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full border border-green-200/50">
              Image
            </span>
          );
        }
        if (mimeType === "application/pdf") {
          return (
            <span className="text-xs font-medium bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full border border-red-200/50">
              PDF
            </span>
          );
        }
        if (mimeType.includes("excel") || mimeType.includes("spreadsheet") || mimeType.includes("sheet")) {
          return (
            <span className="text-xs font-medium bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-200/50">
              Excel
            </span>
          );
        }
        if (mimeType.includes("word") || mimeType.includes("document")) {
          return (
            <span className="text-xs font-medium bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400 px-2 py-0.5 rounded-full border border-blue-200/50">
              Word
            </span>
          );
        }
        return (
          <span className="text-xs font-medium bg-slate-50 dark:bg-slate-800 text-slate-500 px-2 py-0.5 rounded-full border border-slate-200/50">
            Document
          </span>
        );
      }
    },
    {
      header: "Document",
      accessor: "title",
      isPrimary: true,
      render: (val, doc) => {
        const mimeType = getMimeType(doc);
        const isImage = mimeType ? mimeType.startsWith("image/") : false;
        const fullPhotoUrl = getFileUrl(doc.photoData);

        return (
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg overflow-hidden border border-slate-200 dark:border-yale-blue-800 bg-slate-100 dark:bg-yale-blue-800 flex items-center justify-center shrink-0">
              {isImage ? (
                <SecureImage
                  src={fullPhotoUrl}
                  alt={doc.title}
                  className="w-full h-full object-cover cursor-pointer"
                  fallbackIcon={Camera}
                  onClick={(e) => {
                    e.stopPropagation();
                    onView(fullPhotoUrl);
                  }}
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center cursor-pointer hover:bg-slate-200 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    onView(fullPhotoUrl);
                  }}
                >
                  <FileText className={`w-6 h-6 ${mimeType === "application/pdf" ? "text-red-500" : "text-slate-400"}`} />
                </div>
              )}
            </div>
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className="text-sm font-semibold text-yale-blue-900 dark:text-slate-100 truncate max-w-[200px]">
                  {doc.title}
                </span>
                {doc.deleted && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-extrabold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 uppercase tracking-wider shrink-0">
                    Deleted
                  </span>
                )}
              </div>
              {doc.description && <span className="text-[10px] text-slate-500 truncate max-w-[200px]">{doc.description}</span>}
            </div>
          </div>
        );
      }
    },
    {
      header: "Project",
      accessor: "projectId",
      render: (val, doc) => (
        <div
          className="text-sm text-yale-blue-600 dark:text-yale-blue-400 hover:text-yale-blue-700 dark:hover:text-yale-blue-300 cursor-pointer font-medium flex items-center gap-1.5 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            if (doc.project?.id || doc.projectId) {
              navigate(`/projects/${doc.project?.id || doc.projectId}`);
            }
          }}
        >
          <Building2 className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate max-w-[150px]">{doc.project?.name || "N/A"}</span>
        </div>
      )
    },
    {
      header: "Uploaded By",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${selectedUser !== "ALL" ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="end">
            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by User</p>
              <LazySearchSelect
                value={selectedUser === "ALL" ? "all" : selectedUser}
                onChange={(val) => onUserChange(val === "all" ? "ALL" : val)}
                apiUrl="/users"
                dataKey="items"
                labelKey="name"
                valueKey="id"
                placeholder="Select user"
                searchPlaceholder="Search users..."
                showAllOption={true}
                allOptionLabel="All Users"
              />
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "uploadedBy",
      render: (val, doc) => (
        <div className="flex items-center gap-2">
          <div className="p-1 bg-slate-100 dark:bg-yale-blue-800 rounded-full">
            <User className="w-3 h-3 text-yale-blue-500" />
          </div>
          <span className="text-sm text-slate-700 dark:text-slate-300">{doc.uploadedBy?.name || "Unknown"}</span>
        </div>
      )
    },
    {
      header: "Location",
      accessor: "location",
      render: (val) => (
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
          <MapPin className="w-3.5 h-3.5 text-slate-400" />
          <span className="truncate max-w-[150px]">{val || "N/A"}</span>
        </div>
      )
    },
    {
      header: "Date",
      filter: (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${dateRange.start || dateRange.end ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Filter className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4" align="end">
            <div className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter by Date Range</p>
              <div className="space-y-2">
                <Label htmlFor="start-date" className="text-[10px]">
                  Start Date
                </Label>
                <Input
                  id="start-date"
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => onDateRangeChange({ ...dateRange, start: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date" className="text-[10px]">
                  End Date
                </Label>
                <Input
                  id="end-date"
                  type="date"
                  value={dateRange.end}
                  min={dateRange.start}
                  disabled={!dateRange.start}
                  onChange={(e) => onDateRangeChange({ ...dateRange, end: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
              <div className="flex justify-end pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => onDateRangeChange({ start: "", end: "" })}
                >
                  Clear Range
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      ),
      accessor: "createdAt",
      sortable: true,
      sortKey: "createdAt",
      render: (val) => (
        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-400">
          <Calendar className="w-3.5 h-3.5 text-yale-blue-500" />
          {val ? formatDate(val) : "N/A"}
        </div>
      )
    },
    {
      header: "Actions",
      filter: permissions.canDelete ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              className={`p-1 rounded-md transition-colors ${showDeleted ? "bg-primary/20 text-primary" : "hover:bg-slate-200 dark:hover:bg-yale-blue-800 text-slate-400"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="w-4 h-4 hidden md:block" />
              <span className="md:hidden">
                {showDeleted ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              </span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3" align="end">
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={showDeleted}
                onCheckedChange={(val) => {
                  setShowDeleted(val);
                  setCurrentPage(0);
                }}
                id="show-deleted-documents-table"
              />
              <Label htmlFor="show-deleted-documents-table" className="text-xs font-semibold cursor-pointer whitespace-nowrap">
                Show Deleted
              </Label>
            </div>
          </PopoverContent>
        </Popover>
      ) : null,
      accessor: "id",
      render: (_, doc) => {
        const fullPhotoUrl = getFileUrl(doc.photoData);
        return (
          <ActionMenu
            canEdit={false}
            canDelete={permissions?.canDelete && !doc.deleted}
            onDelete={() => onDelete(doc.id)}
            customActions={
              <DropdownMenuItem className="cursor-pointer" onClick={() => onView(fullPhotoUrl)}>
                <Eye className="w-4 h-4 mr-2" /> View Document
              </DropdownMenuItem>
            }
          />
        );
      }
    }
  ];

  return (
    <DataTable
      columns={documentColumns}
      data={documents}
      emptyStateTitle="No documents found"
      emptyStateDescription="Upload site photos, schematics, or reports to track project progress."
      emptyStateIcon={Camera}
      sortBy={sortBy}
      direction={direction}
      onSort={onSort}
    />
  );
};

export default DocumentTable;
